import { useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDownIcon, MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import { QuinnChatSummary, QuinnCopy } from "../../types";
import { groupChats } from "../../utils/chatHistory.ts";
import { ChatRow } from "./ChatRow.tsx";

type QuinnHistoryMenuProps = {
    chats: QuinnChatSummary[];
    activeChatId: string | null;
    title: string;
    onOpenChat: (chatId: string) => void;
    onNewChat: () => void;
};

export function QuinnHistoryMenu({ chats, activeChatId, title, onOpenChat, onNewChat }: QuinnHistoryMenuProps) {
    const [search, setSearch] = useState("");

    const groups = groupChats(chats, search);

    return (
        <Popover className="relative min-w-0 flex-1">
            <PopoverButton className="flex w-full min-w-0 items-center gap-1.5 rounded-lg border border-transparent px-2 py-1 text-left transition-colors hover:border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-0 data-[open]:border-transparent data-[open]:bg-gray-50 data-[focus]:outline-none data-[focus]:ring-0">
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-gray-900">{title}</span>
                <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            </PopoverButton>

            <PopoverPanel
                anchor={{ to: "bottom start", gap: 6 }}
                className="z-[60] w-[300px] rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
            >
                {({ close }) => (
                    <>
                        <div className="flex items-center gap-1.5 border-b border-gray-100 px-2.5 py-2">
                            <MagnifyingGlassIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder={QuinnCopy.SearchChats}
                                className="w-full border-0 bg-transparent p-0 text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                            />
                        </div>

                        <div className="scrollbar-subtle max-h-72 overflow-y-auto py-1">
                            {groups.map((group) => (
                                <div key={group.label}>
                                    <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400">
                                        {group.label}
                                    </p>
                                    {group.items.map((chat) => (
                                        <ChatRow
                                            key={chat.id}
                                            chat={chat}
                                            isActive={chat.id === activeChatId}
                                            onSelect={() => {
                                                onOpenChat(chat.id);
                                                close();
                                            }}
                                        />
                                    ))}
                                </div>
                            ))}

                            {groups.length === 0 && (
                                <p className="px-2.5 py-3 text-xs text-gray-400">Nog geen eerdere gesprekken</p>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                onNewChat();
                                close();
                            }}
                            className="flex w-full items-center gap-2 border-t border-gray-100 px-2.5 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                        >
                            <PlusIcon className="h-3.5 w-3.5 text-gray-400" />
                            {QuinnCopy.NewChat}
                        </button>
                    </>
                )}
            </PopoverPanel>
        </Popover>
    );
}
